import { useEffect, useState } from 'react'
import { Link, useSearchParams, useNavigate, useParams } from 'react-router-dom'
import { Button, Card, Spinner } from '@/components/ui'
import { getPlatformDisplayName } from '@/services/platforms'
import type { AccountPlatform } from '@/services/types'

type CallbackState = 'loading' | 'success' | 'error'

const ERROR_MESSAGES: Record<string, string> = {
  access_denied: 'You declined to connect your account.',
  invalid_request: 'The authorization request was invalid. Please try again.',
  server_error: 'The platform encountered an error. Please try again later.',
  default: 'Failed to connect your account. Please try again.',
}

interface PlatformCallbackPageProps {
  platform?: AccountPlatform
}

export function PlatformCallbackPage({ platform: platformProp }: PlatformCallbackPageProps) {
  const { platform: platformParam } = useParams<{ platform: string }>()
  const platform = (platformProp ?? platformParam ?? 'twitter') as AccountPlatform
  const platformName = getPlatformDisplayName(platform)

  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [state, setState] = useState<CallbackState>('loading')
  const [errorMessage, setErrorMessage] = useState<string>('')

  const success = searchParams.get('success')
  const error = searchParams.get('error')

  useEffect(() => {
    if (success === 'true') {
      setState('success')
      const timer = setTimeout(() => {
        navigate('/accounts', { replace: true })
      }, 3000)
      return () => clearTimeout(timer)
    } else if (error) {
      setState('error')
      setErrorMessage(ERROR_MESSAGES[error] || ERROR_MESSAGES.default)
    } else {
      setState('error')
      setErrorMessage('Invalid callback. Please try connecting again.')
    }
  }, [success, error, navigate])

  return (
    <div className="min-h-screen flex items-center justify-center bg-primary-bg px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="text-2xl font-medium">
            Beacon
          </Link>
        </div>

        <Card>
          {state === 'loading' && (
            <div className="flex flex-col items-center py-8">
              <Spinner size="lg" />
              <p className="text-secondary-text mt-4">Connecting your {platformName} account...</p>
            </div>
          )}

          {state === 'success' && (
            <div className="text-center py-4">
              <div className="mb-4">
                <svg
                  className="mx-auto h-12 w-12 text-success"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h2 className="text-lg font-medium mb-2">{platformName} Connected!</h2>
              <p className="text-secondary-text text-sm mb-6">
                Your {platformName} account has been successfully connected. You can now analyze your voice and post content.
              </p>
              <p className="text-xs text-secondary-text mb-4">
                Redirecting to accounts in a moment...
              </p>
              <Button onClick={() => navigate('/accounts', { replace: true })}>
                Go to Accounts
              </Button>
            </div>
          )}

          {state === 'error' && (
            <div className="text-center py-4">
              <div className="mb-4">
                <svg
                  className="mx-auto h-12 w-12 text-error"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </div>
              <h2 className="text-lg font-medium mb-2">Connection Failed</h2>
              <p className="text-secondary-text text-sm mb-6">{errorMessage}</p>
              <div className="flex gap-3 justify-center">
                <Link to="/accounts">
                  <Button variant="secondary">Back to Accounts</Button>
                </Link>
                <Link to="/accounts">
                  <Button>Try Again</Button>
                </Link>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  )
}
